import { Component, Show, createMemo, mergeProps } from 'solid-js';

export interface SpinnerProps {
  /**
   * The name of the spinner, used for identification purposes.
   */
  name?: string;
  /**
   * Passing in an `onCancel` function will cause the Spinner to render a
   * cancel button. The callback function to be called when the cancel button is clicked.
   * and should handle the cancellation of the loading process.
   */
  onCancel?(): void;
  /**
   * Size of the spinner in pixels. Controls width and height of the
   * container. Defaults to 24 if not provided.
   */
  size?: number;
  /**
   * Custom spinner color as a hex value eg #ABC123
   */
  color?: string;
}

export const Spinner: Component<SpinnerProps> = (props) => {
  // Set spinner size to 24px if not provided
  const mergedProps = mergeProps({ size: 24, color: 'blue' }, props);
  // Set cancel button size to 60% of the spinner size
  // eslint-disable-next-line no-magic-numbers
  const cancelButtonSize = createMemo(() => mergedProps.size * 0.6);

  return (
    <>
      <div
        data-testid={props.name ? `spinner-${props.name}` : null}
        class="relative flex items-center justify-center trimble-file-state"
        style={{
          width: `${mergedProps.size}px`,
          height: `${mergedProps.size}px`,
        }}
      >
        <Show when={props.onCancel}>
          <button
            data-testid={props.name ? `spinner-cancel-${props.name}` : null}
            onClick={() => props.onCancel?.()}
            class="absolute cursor-pointer z-10 flex rounded-full p-1 items-center bg-gray-8 justify-center"
            style={{
              width: `${cancelButtonSize()}px`,
              height: `${cancelButtonSize()}px`,
            }}
          >
            <modus-icon
              class="flex justify-center items-center"
              size={`${cancelButtonSize()}`}
              color="white"
              name="close"
            />
          </button>
        </Show>
        <div
          class="animate-spin border-[0.3rem] border-solid border-gray-1 !border-t-[--spinnerColor] rounded-full"
          style={{
            width: `${mergedProps.size}px`,
            height: `${mergedProps.size}px`,
            display: 'inline-block',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '--spinnerColor': mergedProps.color,
          }}
        />
      </div>
    </>
  );
};
